import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import cls from 'classnames/bind';

import addDays from 'date-fns/addDays';
import isBefore from 'date-fns/isBefore';
import isSameDay from 'date-fns/isSameDay';
import isWithinInterval from 'date-fns/isWithinInterval';
import noop from 'lodash/noop';

import { SATURDAY_INDEX, SUNDAY_INDEX } from '../../../../calendarConstants';
import Day from '../Day/Day';

import s from './Week.scss';

const cx = cls.bind(s);

class Week extends PureComponent {
  static propTypes = {
    days: PropTypes.array,

    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    hoveredDate: PropTypes.instanceOf(Date),

    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    maxDays: PropTypes.number,
    maxDaysTooltipText: PropTypes.string,

    pickOneDate: PropTypes.bool,

    endOnlyAfterStart: PropTypes.bool,
    checkIfDateIsDisabled: PropTypes.func,

    onDateSelect: PropTypes.func,
    onDateHover: PropTypes.func,

    heatmap: PropTypes.shape({
      currency: PropTypes.string.isRequired,
      min: PropTypes.number.isRequired,
      mid: PropTypes.number.isRequired,
      max: PropTypes.number.isRequired,
      time: PropTypes.number.isRequired,
      prices: PropTypes.PropTypes.object.isRequired,
    }),
    heatmapStatus: PropTypes.shape({
      initial: PropTypes.bool.isRequired,
      loading: PropTypes.bool.isRequired,
      success: PropTypes.bool.isRequired,
      error: PropTypes.bool.isRequired,
      cancelled: PropTypes.bool.isRequired,
    }),
    withHeatmap: PropTypes.bool,
    showHeatmapPrices: PropTypes.bool,
  };

  static defaultProps = {};

  constructor(props, ctx) {
    super(props, ctx);
  }

  getAvailableDatesRange() {
    const { startDate, maxDays, endOnlyAfterStart } = this.props;

    if (!startDate || !maxDays) {
      // needs update
      return {};
    }

    const daysOffset = maxDays - 1;

    return {
      rangeStartDate: endOnlyAfterStart ? startDate : addDays(startDate, -daysOffset),
      rangeEndDate: addDays(startDate, daysOffset),
    };
  }

  getMods(dayData, i, days) {
    const {
      startDate,
      endDate,
      hoveredDate,
      pickOneDate,
      minDate,
      maxDate,
      checkIfDateIsDisabled,
    } = this.props;

    const currentDate = new Date(dayData.year, dayData.month, dayData.day);

    const isStartDate = isSameDay(currentDate, startDate);
    const isEndDate = isSameDay(currentDate, endDate);
    const isSelected = pickOneDate ? isStartDate : isStartDate || isEndDate;
    const isDoubleSelected = isEndDate && isStartDate;

    let isInSelectedRange = false;
    if (!pickOneDate && startDate && endDate) {
      isInSelectedRange = isWithinInterval(currentDate, { start: startDate, end: endDate });
    }

    const isHovered = Boolean(!isSelected && isSameDay(currentDate, hoveredDate));

    let isInHoveredRange = false;
    if (!pickOneDate && hoveredDate && startDate && !endDate) {
      isInHoveredRange = isBefore(hoveredDate, startDate)
        ? isWithinInterval(currentDate, { start: hoveredDate, end: startDate })
        : isWithinInterval(currentDate, { start: startDate, end: hoveredDate });
    }

    let isDisabled = !isWithinInterval(currentDate, { start: minDate, end: maxDate });
    if (!isDisabled && !pickOneDate && startDate && !endDate) {
      const { rangeStartDate, rangeEndDate } = this.getAvailableDatesRange();
      if (rangeStartDate && rangeEndDate) {
        isDisabled = !isWithinInterval(currentDate, { start: rangeStartDate, end: rangeEndDate });
      }
    }

    if (!isDisabled && checkIfDateIsDisabled) {
      isDisabled = checkIfDateIsDisabled(currentDate, this.props);
    }

    const mods = {
      isSelected,
      isDoubleSelected,
      isInSelectedRange,
      isHovered,
      isInHoveredRange,
      isMinDate: isSameDay(currentDate, minDate),
      isMaxDate: isSameDay(currentDate, maxDate),
      isWeekend: dayData.weekDay === SATURDAY_INDEX || dayData.weekDay === SUNDAY_INDEX,
      isDisabled,
    };

    let hasLeftRounding = false;
    let hasRightRounding = false;

    if (isInSelectedRange) {
      hasLeftRounding = isStartDate;
      hasRightRounding = isEndDate;
    } else if (isInHoveredRange) {
      if (isStartDate) {
        if (isBefore(currentDate, hoveredDate)) {
          hasLeftRounding = true;
        } else if (isBefore(hoveredDate, currentDate)) {
          hasRightRounding = true;
        } else {
          // ховер по выбранной дате
          hasLeftRounding = true;
          hasRightRounding = true;
        }
      } else if (isHovered) {
        if (isBefore(hoveredDate, startDate)) {
          hasLeftRounding = true;
        } else if (isBefore(startDate, hoveredDate)) {
          hasRightRounding = true;
        }
      }
    }

    if (dayData) {
      // соседствует со скрытым днём соседнего месяца
      if (days[i - 1] === false) {
        hasLeftRounding = true;
      } else if (days[i + 1] === false) {
        hasRightRounding = true;
      }
    }

    Object.assign(mods, {
      hasLeftRounding,
      hasRightRounding,
    });

    return mods;
  }

  render() {
    const {
      days,
      onDateHover,
      onDateSelect,
      heatmap,
      heatmapStatus,
      withHeatmap,
      showHeatmapPrices,
      maxDays,
      endOnlyAfterStart,
      startDate,
      endDate,
      maxDaysTooltipText,
    } = this.props;

    return (
      <div className={cx('Week')}>
        {days.map((dayData, i, days) => {
          const modifiers = this.getMods(dayData, i, days);

          const isLaterThanStartDate =
            !!startDate &&
            !!dayData &&
            startDate < new Date(dayData.year, dayData.month, dayData.day);

          const shouldShowMaxDaysTooltip =
            !!startDate &&
            !!dayData &&
            !endDate &&
            modifiers.isDisabled &&
            (isLaterThanStartDate || !endOnlyAfterStart);

          return (
            <Day
              ref={(ref) => (this[dayData.day] = ref)}
              {...dayData}
              {...modifiers}
              isHidden={!dayData}
              onDateHover={onDateHover}
              onDateSelect={onDateSelect}
              onTouchStart={noop}
              onMouseDown={noop}
              key={i}
              heatmap={heatmap}
              heatmapStatus={heatmapStatus}
              withHeatmap={withHeatmap}
              showHeatmapPrices={showHeatmapPrices}
              maxDays={maxDays}
              shouldShowMaxDaysTooltip={shouldShowMaxDaysTooltip}
              maxDaysTooltipText={maxDaysTooltipText}
            />
          );
        })}
      </div>
    );
  }
}

export default Week;
